// IssueDetailDrawer — the right-column detail surface for the selected issue.
// Header: the title plus three icon buttons in priority order — Download PDF
// (kicks off the async per-issue export pipeline + polls until the artefact
// is ready, then triggers an authenticated blob download via exportApi so the
// S3 host is never exposed), Edit (opens the metadata form), and Update
// status (the primary brand-coloured action; opens a small menu of the
// transitions allowed from the current status, plus a Close issue… entry that
// opens the CloseIssueDialog). The meta row carries the ref, severity, status
// pill + duration tag, and the raised date; a closed issue also shows its
// outcome as a sub-label so the audit trail reads "why it ended". Below: the
// owner / raised-by / contract-reference fields, the linked-items chip strip
// (each chip is a button that fires onNavigateToLinkedItem to jump to that
// register tab), the thread, and — for a writer — the composer.
//
// Scroll strategy: the drawer is a flex column that fills the viewport-bounded
// parent column set by IssueTab (h-full, min-h-0, flex-col). Three regions:
// (1) header + fields — shrink-0, always visible; (2) thread — flex-auto
// min-h-0 overflow-y-auto, grows to fill remaining height and scrolls when the
// thread is long; uses flex-auto (not flex-1) so short threads shrink from
// natural content size rather than from 0, keeping the composer close; (3)
// composer — shrink-0, pinned at the bottom. All write affordances are hidden
// for a read-only viewer; the API is editor-gated server-side.

import { useEffect, useMemo, useRef, useState } from "react";
import { Download, Loader2, Pencil, RefreshCw } from "lucide-react";
import { useUserDisplays } from "../../../../../hooks/useUsers";
import { useClickOutside } from "../../../../../hooks/useClickOutside";
import { useTrackingItems } from "../../../../../hooks/useTracking";
import { labelForIssueLink } from "../labelForIssueLink";
import {
  useRequestIssueExport,
  useExport,
} from "../../../../../hooks/useContractExports";
import { exportApi } from "../../../../../services/api";
import TrackingStatusBadge from "../../shared/TrackingStatusBadge";
import IssueSeverity from "../IssueSeverity";
import IssueDurationTag from "../IssueDurationTag";
import IssueThread from "./IssueThread";
import IssueComposer from "./IssueComposer";
import CloseIssueDialog from "./CloseIssueDialog";
import { allowedIssueTransitions } from "../canTransitionIssue";
import { deriveIssueDisplay } from "../deriveIssueDisplay";
import { getBrisbaneToday } from "../../../../../utils/getBrisbaneToday";
import { toBrisbaneDay } from "../../../../../utils/toBrisbaneDay";
import type {
  AppendThreadActionInput,
  Deliverable,
  IssueCloseCommand,
  IssueCloseOutcome,
  IssueLinkTargetType,
  IssueStatus,
  Issue,
  Kpi,
  KpiMeasurement,
  Milestone,
} from "../../../../../types";

// Label vocabulary for the linked-items strip in the drawer body. Keep in
// step with IssueLinkPicker / OpenIssueCountBadge — the three surfaces tell
// the same story about the cross-register link target type.
const LINKED_TARGET_LABEL: Record<IssueLinkTargetType, string> = {
  deliverable: "Deliverable",
  milestone: "Milestone",
  kpi: "KPI",
  "kpi-measurement": "KPI period",
};

const STATUS_LABEL: Record<IssueStatus, string> = {
  open: "Open",
  in_progress: "In progress",
  awaiting_response: "Awaiting response",
  resolved: "Resolved",
  closed: "Closed",
};

const OUTCOME_LABEL: Record<IssueCloseOutcome, string> = {
  resolved: "Resolved",
  superseded: "Superseded",
  withdrawn: "Withdrawn",
  unresolved_on_record: "Unresolved (on record)",
};

function toIsoDay(d: Date): string {
  return new Date(
    Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()),
  )
    .toISOString()
    .slice(0, 10);
}

function formatDate(iso: string): string {
  const d = new Date(`${iso.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

export interface IssueDetailDrawerProps {
  issue: Issue;
  /** The contract the issue belongs to — required for the export pipeline
   *  (the request endpoint is contract-scoped). Passed from IssueTab. */
  contractId: string;
  today?: Date;
  canWrite: boolean;
  isMutating: boolean;
  onEditIssue: () => void;
  /** Emits the target status + optional note; the orchestrator folds in the
   *  issue version for the optimistic-lock transition payload. */
  onTransition: (input: { to: IssueStatus; note?: string }) => void;
  onClose: (input: IssueCloseCommand) => void;
  onAddComment: (text: string) => void;
  onAddAction: (input: AppendThreadActionInput) => void;
  onMarkActionDone: (entryId: string) => void;
  /** Navigates to a linked register item by setting the tracking tab + item
   *  deep-link params. Threaded down from ManagedContractDetail → IssueTab.
   *  Omitting it leaves chips as non-interactive (graceful degradation). */
  onNavigateToLinkedItem?: (
    targetType: IssueLinkTargetType,
    targetId: string,
  ) => void;
}

export default function IssueDetailDrawer({
  issue,
  contractId,
  today,
  canWrite,
  isMutating,
  onEditIssue,
  onTransition,
  onClose,
  onAddComment,
  onAddAction,
  onMarkActionDone,
  onNavigateToLinkedItem,
}: IssueDetailDrawerProps) {
  const [statusMenuOpen, setStatusMenuOpen] = useState(false);
  const [closeDialogOpen, setCloseDialogOpen] = useState(false);

  // The status menu dismisses on an outside click or Escape — without this it
  // only closed via the toggle or a menu item, trapping the user on a stray
  // open. The toggle button is included so a click on it dismisses via its own
  // onClick rather than being treated as "outside".
  const statusButtonRef = useRef<HTMLButtonElement>(null);
  const statusMenuRef = useRef<HTMLDivElement>(null);
  useClickOutside(statusMenuOpen, [statusButtonRef, statusMenuRef], () =>
    setStatusMenuOpen(false),
  );

  // Per-issue PDF export flow. The Download button triggers the request
  // mutation; the resulting exportId feeds useExport which polls until the
  // worker marks the row ready. On 'ready' we fetch the presigned URL and
  // open it. On 'failed' the banner shows the failure reason. exportId
  // resets when the user clicks Download again so a second click queues a
  // fresh job rather than re-using the prior one.
  const requestExport = useRequestIssueExport(contractId);
  const [exportId, setExportId] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const exportQuery = useExport(contractId, exportId ?? undefined);

  useEffect(() => {
    if (!exportId) return;
    const data = exportQuery.data;
    if (!data) return;
    if (data.status === "ready") {
      // Authenticated blob download — delegates to downloadExport which handles
      // both the binary-stream path (synthetic anchor click) and the JSON { url }
      // fallback transparently. Clears local state on completion so the button
      // returns to idle and a second click queues a fresh job.
      void exportApi
        .downloadExport(contractId, exportId, "pdf")
        .catch(() => {
          setExportError("Could not start the download. Please try again.");
        })
        .finally(() => {
          setExportId(null);
        });
    /* eslint-disable react-hooks/set-state-in-effect -- the ready branch
       triggers exportApi.downloadExport (a real async side effect that initiates
       a binary blob download), so this effect cannot be converted to Pattern 0.
       The failed branch shares the effect body; its synchronous setState calls
       are inseparable from the overall polling state machine. */
    } else if (data.status === "failed") {
      setExportError(data.failedReason ?? "PDF export failed. Please try again.");
      setExportId(null);
    }
    /* eslint-enable react-hooks/set-state-in-effect */
  }, [exportId, exportQuery.data, contractId]);

  const isExporting = requestExport.isPending || exportId !== null;

  const handleDownloadPdf = (): void => {
    if (isExporting) return;
    setExportError(null);
    requestExport.mutate(issue.id, {
      onSuccess: (res) => {
        setExportId(res.exportId);
      },
      onError: () => {
        setExportError("Could not queue the PDF export. Please try again.");
      },
    });
  };

  const peopleIds = useMemo(
    () =>
      Array.from(
        new Set(
          [issue.ownerId, issue.createdBy].filter(
            (id): id is string => !!id,
          ),
        ),
      ),
    [issue.ownerId, issue.createdBy],
  );
  const { data: people } = useUserDisplays(peopleIds);

  // Resolve each cross-register link to its display name (mirrors
  // IssueLinkPicker via the shared labelForIssueLink). Each register query is
  // enabled only when a link of that type is present, so a drawer with no
  // links — or only deliverable links — fires no superfluous list fetches; the
  // resolver falls back to the ULID if the target was deleted.
  const linkedTypes = useMemo(
    () => new Set((issue.links ?? []).map((l) => l.targetType)),
    [issue.links],
  );
  const deliverableQuery = useTrackingItems(contractId, "deliverable", undefined, {
    enabled: linkedTypes.has("deliverable"),
  });
  const milestoneQuery = useTrackingItems(contractId, "milestone", undefined, {
    enabled: linkedTypes.has("milestone"),
  });
  const kpiQuery = useTrackingItems(contractId, "kpi", undefined, {
    enabled: linkedTypes.has("kpi"),
  });
  const measurementQuery = useTrackingItems(contractId, "kpi-measurement", undefined, {
    enabled: linkedTypes.has("kpi-measurement"),
  });
  const linkRegisters = useMemo(
    () => ({
      deliverables: (deliverableQuery.data?.items ?? []).filter(
        (i): i is Deliverable => i.type === "deliverable",
      ),
      milestones: (milestoneQuery.data?.items ?? []).filter(
        (i): i is Milestone => i.type === "milestone",
      ),
      kpis: (kpiQuery.data?.items ?? []).filter((i): i is Kpi => i.type === "kpi"),
      measurements: (measurementQuery.data?.items ?? []).filter(
        (i): i is KpiMeasurement => i.type === "kpi-measurement",
      ),
    }),
    [deliverableQuery.data, milestoneQuery.data, kpiQuery.data, measurementQuery.data],
  );

  // Brisbane calendar day (matching backend + parity fixtures), not wall-clock;
  // a fixed `today` Date is injected by tests for determinism.
  const todayIso = today ? toIsoDay(today) : getBrisbaneToday();
  // raisedAt / statusEnteredAt are full ISO instants from the backend; the
  // derivation expects Brisbane calendar days, so normalise before passing.
  const { durationInStateDays } = deriveIssueDisplay({
    today: todayIso,
    raisedAt: toBrisbaneDay(issue.raisedAt),
    statusEnteredAt: toBrisbaneDay(issue.statusEnteredAt),
  });

  const ownerName = issue.ownerId
    ? (people.get(issue.ownerId)?.name ?? issue.ownerId)
    : "Unassigned";
  const raisedByName = issue.createdBy
    ? (people.get(issue.createdBy)?.name ?? issue.createdBy)
    : "—";

  // The transitions the header menu offers — excludes 'closed', which goes
  // through the dialog so a close always captures its outcome reason.
  const menuTargets = allowedIssueTransitions(issue.status).filter(
    (s) => s !== "closed",
  );
  const canClose = allowedIssueTransitions(issue.status).includes("closed");

  const handleMenuTransition = (to: IssueStatus) => {
    setStatusMenuOpen(false);
    onTransition({ to });
  };

  const handleCloseConfirm = (input: IssueCloseCommand) => {
    setCloseDialogOpen(false);
    onClose(input);
  };

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="border-b border-slate-100 p-4 dark:border-slate-700/60">
        <div className="flex items-start justify-between gap-3">
          <h2 className="flex-1 text-h4 font-bold text-slate-900 dark:text-slate-100">
            {issue.title}
          </h2>
          <div className="flex shrink-0 items-center gap-1.5">
            <button
              type="button"
              onClick={handleDownloadPdf}
              disabled={isExporting}
              aria-label={
                isExporting ? "Generating PDF — please wait" : "Download PDF"
              }
              aria-busy={isExporting}
              title={
                isExporting
                  ? "Generating PDF — please wait"
                  : "Download a PDF of this issue"
              }
              className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 disabled:cursor-progress disabled:opacity-70 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700/40"
              data-testid="issue-download-pdf"
            >
              {isExporting ? (
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              ) : (
                <Download className="h-4 w-4" aria-hidden="true" />
              )}
            </button>
            {canWrite && (
              <button
                type="button"
                onClick={onEditIssue}
                aria-label="Edit issue"
                className="flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 text-slate-500 hover:bg-slate-50 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-700/40"
              >
                <Pencil className="h-4 w-4" aria-hidden="true" />
              </button>
            )}
            {canWrite && (menuTargets.length > 0 || canClose) && (
              <div className="relative">
                <button
                  ref={statusButtonRef}
                  type="button"
                  onClick={() => setStatusMenuOpen((o) => !o)}
                  aria-label="Update status"
                  aria-haspopup="menu"
                  aria-expanded={statusMenuOpen}
                  className="flex h-8 w-8 items-center justify-center rounded-md bg-brand-600 text-white hover:bg-brand-500 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-1"
                >
                  <RefreshCw className="h-4 w-4" aria-hidden="true" />
                </button>
                {statusMenuOpen && (
                  <div
                    ref={statusMenuRef}
                    role="menu"
                    className="absolute right-0 z-20 mt-1 min-w-[200px] rounded-md border border-slate-200 bg-white p-1 shadow-soft-lg dark:shadow-soft-lg-dark dark:border-slate-700 dark:bg-slate-800"
                  >
                    {menuTargets.map((s) => (
                      <button
                        key={s}
                        type="button"
                        role="menuitem"
                        onClick={() => handleMenuTransition(s)}
                        className="block w-full rounded px-2 py-1.5 text-left text-body-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700"
                      >
                        Move to {STATUS_LABEL[s]}
                      </button>
                    ))}
                    {canClose && (
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setStatusMenuOpen(false);
                          setCloseDialogOpen(true);
                        }}
                        className="block w-full rounded px-2 py-1.5 text-left text-body-sm text-slate-700 hover:bg-slate-50 dark:text-slate-200 dark:hover:bg-slate-700"
                      >
                        Close issue…
                      </button>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2 text-caption text-slate-500 dark:text-slate-400">
          <span className="font-mono">{issue.issueRef}</span>
          <IssueSeverity severity={issue.severity} />
          <TrackingStatusBadge kind="issue" status={issue.status} />
          <IssueDurationTag
            status={issue.status}
            durationDays={durationInStateDays}
          />
          <span aria-hidden="true">·</span>
          <span data-testid="issue-raised-date">
            Raised {formatDate(issue.raisedAt)}
          </span>
        </div>

        {issue.status === "closed" && issue.closeOutcome && (
          <div
            data-testid="issue-close-outcome"
            className="mt-1 text-caption text-slate-500 dark:text-slate-400"
          >
            Closed · {OUTCOME_LABEL[issue.closeOutcome]}
            {issue.outcomeNote ? ` — ${issue.outcomeNote}` : ""}
          </div>
        )}

        {exportError && (
          <div
            role="alert"
            data-testid="issue-export-error"
            className="mt-2 rounded-md border border-red-200 bg-red-50 px-2 py-1.5 text-caption text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200"
          >
            {exportError}{" "}
            <button
              type="button"
              onClick={() => setExportError(null)}
              className="font-medium underline focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400"
            >
              Dismiss
            </button>
          </div>
        )}
      </div>

      {/* Region 1: field block — always visible, never scrolls */}
      <div className="shrink-0 p-4 pb-0">
        <dl className="mb-5 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-body-sm">
          <dt className="text-slate-500 dark:text-slate-400">Owner</dt>
          <dd className="font-medium text-slate-700 dark:text-slate-200">
            {ownerName}
          </dd>
          <dt className="text-slate-500 dark:text-slate-400">Raised by</dt>
          <dd className="font-medium text-slate-700 dark:text-slate-200">
            {raisedByName}
          </dd>
          {issue.contractReference && (
            <>
              <dt className="text-slate-500 dark:text-slate-400">
                Contract reference
              </dt>
              <dd className="font-medium text-slate-700 dark:text-slate-200">
                {issue.contractReference}
              </dd>
            </>
          )}
          {issue.links && issue.links.length > 0 && (
            <>
              <dt className="text-slate-500 dark:text-slate-400">Linked to</dt>
              <dd
                data-testid="issue-linked-to"
                className="text-slate-700 dark:text-slate-200"
              >
                <ul className="flex flex-wrap gap-1.5">
                  {issue.links.map((link) => {
                    const chipKey = `${link.targetType}#${link.targetId}`;
                    const chipContent = (
                      <>
                        <span className="font-medium text-slate-600 dark:text-slate-300">
                          {LINKED_TARGET_LABEL[link.targetType]}:
                        </span>
                        <span className="text-slate-600 dark:text-slate-300">
                          {labelForIssueLink(link, linkRegisters)}
                        </span>
                      </>
                    );
                    const chipClass =
                      "inline-flex items-center gap-1 rounded-md border border-slate-200 bg-slate-50 px-2 py-0.5 text-caption dark:border-slate-700 dark:bg-slate-800/60";
                    return (
                      <li key={chipKey}>
                        {onNavigateToLinkedItem ? (
                          <button
                            type="button"
                            data-testid={`linked-chip-${chipKey}`}
                            onClick={() => {
                              // kpi-measurement links open the KPI tab with the
                              // parent KPI's id (the KPI tab only accepts a KPI
                              // id via ?item=, not a period/measurement id). We
                              // resolve the parentId from the loaded measurements
                              // register. When the measurement hasn't loaded yet
                              // or was deleted, fall back to opening the KPI tab
                              // without an item deep-link so the user at least
                              // lands on the right tab.
                              if (link.targetType === "kpi-measurement") {
                                const measurement = linkRegisters.measurements.find(
                                  (m) => m.id === link.targetId,
                                );
                                const kpiId = measurement?.parentId ?? null;
                                onNavigateToLinkedItem(
                                  "kpi-measurement",
                                  kpiId ?? link.targetId,
                                );
                              } else {
                                onNavigateToLinkedItem(
                                  link.targetType,
                                  link.targetId,
                                );
                              }
                            }}
                            className={`${chipClass} cursor-pointer hover:border-brand-300 hover:bg-brand-50 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 dark:hover:border-brand-700 dark:hover:bg-brand-950/40`}
                          >
                            {chipContent}
                          </button>
                        ) : (
                          <span className={chipClass}>{chipContent}</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </dd>
            </>
          )}
        </dl>
      </div>

      {/* Region 2: thread — flex-auto so it takes remaining height and scrolls
          when the thread is long; shrinks from its natural content size rather
          than from 0 (flex-auto, not flex-1) so short threads stay compact and
          don't leave a gap before the composer below. */}
      <div
        data-testid="thread-scroll-wrapper"
        className="flex-auto min-h-0 overflow-y-auto px-4 pb-2 pt-4"
      >
        {/* slate-500 (not slate-400) for WCAG AA on the white drawer surface. */}
        <div className="mb-2 text-overline font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
          Thread
        </div>
        <div className="pr-1">
          <IssueThread
            entries={issue.threadEntries ?? []}
            actions={issue.actions}
            today={today}
            canWrite={canWrite}
            onMarkActionDone={onMarkActionDone}
          />
        </div>
      </div>

      {/* Region 3: composer — shrink-0 so it stays pinned at the bottom of the
          bounded column and is never scrolled out of view. */}
      {canWrite && (
        <div className="shrink-0 px-4 pb-4 pt-2">
          <IssueComposer
            currentStatus={issue.status}
            onAddComment={onAddComment}
            onTransition={onTransition}
            onAddAction={onAddAction}
            isSubmitting={isMutating}
          />
        </div>
      )}

      {closeDialogOpen && (
        <CloseIssueDialog
          onClose={() => setCloseDialogOpen(false)}
          onConfirm={handleCloseConfirm}
          isSubmitting={isMutating}
        />
      )}
    </div>
  );
}
